<template>
  <div class="form-wrapper">
    <h2>Registrierung</h2>

    <!-- Schritt 1: Formular zur Anforderung des Bestätigungscodes -->
    <form v-if="!isCodeStep" @submit.prevent="handleRequestCode">
      <div class="form-group">
        <label for="email">E-Mail:</label>
        <input type="email" id="email" v-model="email" required />
      </div>
      <div class="form-group">
        <label for="password">Passwort:</label>
        <input type="password" id="password" v-model="password" required />
      </div>
      <button type="submit" :disabled="loading">{{ loading ? 'Wird gesendet...' : 'Bestätigungscode anfordern' }}</button>
    </form>

    <!-- Schritt 2: Formular zur Eingabe des per E-Mail erhaltenen Codes -->
    <form v-else @submit.prevent="handleVerifyCode">
      <p>
        Wir haben einen Code an <strong>{{ email }}</strong> gesendet. Bitte gib ihn hier ein.
      </p>
      <div class="form-group">
        <label for="code">Bestätigungscode:</label>
        <input type="text" id="code" v-model="code" required />
      </div>
      <button type="submit" :disabled="loading">{{ loading ? 'Registrierung läuft...' : 'Registrierung abschließen' }}</button>
    </form>
    <!-- Trennlinie für alternative Registrierungsmethoden -->
    <div class="divider">
      <span>oder</span>
    </div>
    <a :href="discordLoginUrl" class="discord-button">
      <svg class="discord-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 127.14 96.36" aria-hidden="true">
        <path fill="currentColor" d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0 105.89 105.89 0 0 0 19.39 8.09C2.79 32.65-1.71 56.6.54 80.21h0A105.73 105.73 0 0 0 32.71 96.36a77.7 77.7 0 0 0 6.89-11.28 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2.05a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2.05a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.27A105.25 105.25 0 0 0 126.6 80.22c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 59.98 31 52.94S36.06 40.2 42.45 40.2c6.44 0 11.63 5.76 11.52 12.74 0 7.04-5.08 12.75-11.52 12.75Zm42.24 0c-6.27 0-11.45-5.71-11.45-12.75S78.3 40.2 84.69 40.2c6.44 0 11.62 5.76 11.52 12.74 0 7.04-5.08 12.75-11.52 12.75Z"/>
      </svg>
      <span>Mit Discord registrieren</span>
    </a>
    <!-- Anzeige von Fehlermeldungen -->
    <pre v-if="errorMessage" class="error">{{ errorMessage }}</pre>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { register, getDiscordURL } from '../services/api';

// Reaktive Zustandsvariablen für die Formular-Eingaben und den UI-Zustand.
const email = ref('');
const password = ref('');
const code = ref('');
const errorMessage = ref('');
const isCodeStep = ref(false); // Zustand für den zweistufigen Prozess
const router = useRouter();

const loading = ref(false)

/**
 * Eine einfache clientseitige Validierung für E-Mail-Adressen.
 * @param {string} email - Die zu prüfende E-Mail.
 * @returns {boolean}
 */
const isValidEmail = (email) => {
  //Regulärer Ausdruck für Email: xD
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Handler für Schritt 1: Fordert einen Bestätigungscode vom Backend an.
 */
const handleRequestCode = async () => {
  errorMessage.value = '';
  loading.value = true;
  try {
    if (!isValidEmail(email.value)) {
      errorMessage.value = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
      return;
    }
    // Ruft die API ohne Code auf, um den ersten Schritt auszulösen.
    const response = await register(email.value, password.value, null);
    console.log('Code angefordert:', response);
    isCodeStep.value = true; // Zum nächsten Schritt wechseln
  } catch (error) {
    errorMessage.value = error.message;
  } finally {
    loading.value = false
  }
};

/**
 * Handler für Schritt 2: Sendet den eingegebenen Code zur Verifizierung an das Backend.
 */
const handleVerifyCode = async () => {
  errorMessage.value = '';
  loading.value = true;
  try {
    // Ruft die API mit dem Code auf, um die Registrierung abzuschließen.
    await register(email.value, password.value, code.value);
    // Leitet bei Erfolg zur Startseite weiter.
    router.push('/'); // Zur Startseite weiterleiten
  } catch (error) {
    errorMessage.value = error.message;
  } finally {
    loading.value = false
  }
};

// Holt die dynamische Discord-Login-URL.
const discordLoginUrl = getDiscordURL();
</script>

<style lang="scss" scoped>
.form-wrapper {
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  background: $card-background;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  h2 {
    margin-top: 0;
    color: $secondary-color;
    text-align: center;
    margin-bottom: 2rem;
  }

  p {
    text-align: center;
    line-height: 1.5;
  }

  .form-group {
    margin-bottom: 1.5rem;
    text-align: left;

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: bold;
      color: $text-color;
    }

    input {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid $border-color;
      border-radius: 4px;
      font-size: 1rem;
      box-sizing: border-box;

      &:focus {
        outline: none;
        border-color: $primary-color;
        box-shadow: 0 0 0 3px rgba($primary-color, 0.2);
      }
    }
  }

  button {
    width: 100%;
    padding: 0.75rem;
    border: none;
    border-radius: 4px;
    background-color: $primary-color;
    color: white;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: $primary-color-hover;
    }
  }

  .error {
    color: red;
    margin-top: 1rem;
    text-align: center;
    white-space: pre-wrap;
    word-break: break-word;
  }
}
.divider {
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: #888;
  font-size: 0.9rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: $border-color;
  }

  span {
    padding: 0 1rem;
  }
}
.discord-button {
  width: 100%;
  padding: 0.75rem;

  border: none;
  border-radius: 4px;

  background-color: #5865F2;
  color: white;

  font-size: 1rem;
  font-weight: bold;
  text-decoration: none;

  cursor: pointer;

  transition:
    background-color 0.2s,
    transform 0.15s;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;

  box-sizing: border-box;

  &:hover {
    background-color: #4752C4;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}
.discord-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
</style>